import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, RotateCcw, Sparkles, Scale, Lock } from 'lucide-react';
import { interactionsApi } from '../../services/api';
import { isTwa } from '../../utils/platform';
import { useStudioStore } from '../../store/studioStore';
import { MemoMessage, InteractionMode } from '../../types';
import { MicButton } from '../ui/MicButton';

interface ChatPanelProps {
  jobId: string;
  mode: InteractionMode;
}

const MODE_COPY = {
  chat: {
    icon: Sparkles,
    emptyTitle: 'Ask anything about this memo',
    emptyHint:
      'Get instant answers grounded in your research — cases, sections, reasoning. Try one of these:',
    suggestions: [
      'Summarise the Brief Answer in 3 bullet points',
      'Which Supreme Court case is the strongest authority here?',
      'Explain this memo in simple language for my client',
      'What are the weakest points in this analysis?',
    ],
    placeholder: 'Ask a follow-up question about your memo…',
  },
  debate: {
    icon: Scale,
    emptyTitle: 'Stress-test your position',
    emptyHint:
      'The AI takes the opposite side of this memo and argues like opposing counsel. Defend your position before the other side does it for real.',
    suggestions: [
      'Present your strongest argument against this memo',
      'Argue this from the respondent’s side',
      'What would opposing counsel attack first?',
    ],
    placeholder: 'Make your argument…',
  },
} as const;

export function ChatPanel({ jobId, mode }: ChatPanelProps) {
  const cacheMessages = useStudioStore((s) => s.setMessages);
  const cached = useStudioStore((s) => s.byKey[`${jobId}:${mode}`]);
  const [messages, setMessages] = useState<MemoMessage[]>(cached ?? []);
  const [input, setInput] = useState('');
  // Only block (with a subtle inline indicator) on the very first load with no
  // cache; otherwise the panel renders instantly from cache.
  const [isLoading, setIsLoading] = useState(cached === undefined);
  const [isSending, setIsSending] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const copy = MODE_COPY[mode];
  const EmptyIcon = copy.icon;

  useEffect(() => {
    let active = true;
    const c = useStudioStore.getState().byKey[`${jobId}:${mode}`];
    if (c) setMessages(c);
    setIsLoading(c === undefined);
    // Revalidate in the background; the (now lightweight) endpoint no longer
    // loads the memo blob, so this is fast.
    interactionsApi
      .getMessages(jobId, mode)
      .then(({ data }) => {
        if (!active) return;
        setMessages(data);
        cacheMessages(jobId, mode, data);
      })
      .catch(() => {
        if (active && c === undefined) setError('Failed to load conversation');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [jobId, mode, cacheMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || isSending) return;
    setError('');
    setInput('');
    setIsSending(true);
    // Optimistic user bubble
    const tempId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: tempId, job_id: jobId, mode, role: 'user', content, created_at: new Date().toISOString() },
    ]);
    try {
      const { data } = await interactionsApi.sendMessage(jobId, mode, content);
      // `messages` (closure) is the pre-optimistic list, so this drops the temp
      // bubble and appends the persisted pair. Keep the cache in sync.
      const next = [...messages, data.user_message, data.assistant_message];
      setMessages(next);
      cacheMessages(jobId, mode, next);
      setRemaining(data.interactions_remaining);
    } catch (err: any) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      if (err.response?.status === 403) {
        setQuotaExhausted(true);
      } else {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
        setInput(content);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Clear this conversation?')) return;
    await interactionsApi.clearMessages(jobId, mode);
    setMessages([]);
    cacheMessages(jobId, mode, []);
  };

  return (
    <div className="bg-white rounded-lg border border-legal-200 flex flex-col" style={{ height: '70vh' }}>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading && messages.length === 0 && (
          <div className="h-full flex items-center justify-center">
            <div className="flex space-x-1.5">
              <span className="h-2 w-2 bg-legal-300 rounded-full animate-bounce" />
              <span className="h-2 w-2 bg-legal-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="h-2 w-2 bg-legal-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        {!isLoading && messages.length === 0 && !isSending && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="h-12 w-12 rounded-full bg-accent-50 flex items-center justify-center mb-4">
              <EmptyIcon className="h-6 w-6 text-accent-600" />
            </div>
            <h3 className="font-semibold text-legal-900 mb-1">{copy.emptyTitle}</h3>
            <p className="text-sm text-legal-500 mb-5 max-w-md">{copy.emptyHint}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {copy.suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-sm px-3 py-1.5 bg-legal-50 border border-legal-200 rounded-full text-legal-700 hover:border-accent hover:text-legal-900 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'user' ? (
              <div className="max-w-[80%] bg-legal-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm whitespace-pre-wrap">
                {m.content}
              </div>
            ) : (
              <div className="max-w-[90%] bg-legal-50 border border-legal-200 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="prose prose-sm prose-slate max-w-none prose-p:text-legal-700 prose-p:my-1.5 prose-strong:text-legal-900 prose-li:text-legal-700 prose-table:text-xs">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))}

        {isSending && (
          <div className="flex justify-start">
            <div className="bg-legal-50 border border-legal-200 rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex space-x-1.5">
                <span className="h-2 w-2 bg-legal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 bg-legal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 bg-legal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quota exhausted upsell */}
      {quotaExhausted && (
        <div className="mx-4 mb-3 p-3 bg-accent-50 border border-accent-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-legal-800">
            <Lock className="h-4 w-4 text-accent-600 shrink-0" />
            <span>You've used all free interactions for this memo.</span>
          </div>
          {isTwa() ? (
            <span className="text-sm text-legal-600 shrink-0">Upgrade at urava.app for unlimited.</span>
          ) : (
            <Link
              to="/pricing"
              data-track="studio-quota-upgrade"
              className="text-sm font-medium px-3 py-1.5 bg-legal-900 text-white rounded-lg hover:bg-legal-800 transition-colors shrink-0"
            >
              Upgrade
            </Link>
          )}
        </div>
      )}

      {error && <p className="mx-4 mb-2 text-sm text-red-600">{error}</p>}

      {/* Composer */}
      <div className="border-t border-legal-200 p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
          className="flex items-end space-x-2"
        >
          {!quotaExhausted && (
            <MicButton
              track="mic-studio"
              onFinal={(t) => setInput((v) => (v ? `${v} ` : '') + t)}
            />
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            placeholder={copy.placeholder}
            rows={1}
            disabled={quotaExhausted}
            className="flex-1 resize-none px-3 py-2.5 border border-legal-300 rounded-lg text-sm text-legal-900 placeholder-legal-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:bg-legal-50"
          />
          <button
            type="submit"
            disabled={!input.trim() || isSending || quotaExhausted}
            className="p-2.5 bg-legal-900 text-white rounded-lg hover:bg-legal-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
        <div className="flex items-center justify-between mt-1.5 px-1">
          <span className="text-xs text-legal-400">
            {remaining !== null && remaining !== undefined
              ? `${remaining} free interaction${remaining === 1 ? '' : 's'} left on this memo`
              : 'Answers are grounded in this memo'}
          </span>
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="flex items-center space-x-1 text-xs text-legal-400 hover:text-legal-700 transition-colors"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
